import Ajv from 'ajv'
import type { ValidateFunction } from 'ajv'
import addFormats from 'ajv-formats'
import type { SegmentTypes } from '~/types/api/rental-segment-types'
import schemasJson from '~/types/api/rental-segment-schemas.json?raw'

let schemas: Record<string, object> | undefined
let ajv: Ajv | undefined
const validators = new Map<string, ValidateFunction>()

/**
 * The tracking plan contains patterns with leading inline flags like `(?im)`,
 * which JavaScript RegExp does not support. Lift them into real flags.
 *
 * @param pattern The JSON Schema pattern
 * @param flags The flags Ajv requests (usually `u`)
 */
function inlineFlagsRegExp(pattern: string, flags: string) {
  const inlineFlags = /^\(\?([a-z]+)\)/.exec(pattern)

  if (!inlineFlags) {
    return new RegExp(pattern, flags)
  }

  const supportedFlags = inlineFlags[1]!.replace(/[^ims]/g, '')

  return new RegExp(pattern.slice(inlineFlags[0].length), [...new Set(flags + supportedFlags)].join(''))
}

inlineFlagsRegExp.code = 'new RegExp'

function getValidator(eventName: string) {
  schemas ??= JSON.parse(schemasJson) as Record<string, object>

  const schema = schemas[eventName]

  if (!schema) {
    return undefined
  }

  if (!ajv) {
    ajv = new Ajv({ allErrors: true, strict: false, code: { regExp: inlineFlagsRegExp } })
    addFormats(ajv)
  }

  let validate = validators.get(eventName)

  if (!validate) {
    validate = ajv.compile(schema)
    validators.set(eventName, validate)
  }

  return validate
}

/**
 * Returns a readable list of tracking plan violations for the serialized
 * payload, or `undefined` when valid.
 *
 * @param eventName The event to check for violations
 * @param payload The payload to validate
 */
export function getSegmentViolations(eventName: keyof SegmentTypes, payload: unknown): string | undefined {
  let validate: ValidateFunction | undefined

  try {
    validate = getValidator(eventName)
  }
  catch (error) {
    return `Schema for '${eventName}' could not be compiled: ${(error as Error).message}`
  }

  if (!validate) {
    return `No schema found for '${eventName}'`
  }

  if (validate(payload)) {
    return undefined
  }

  return (validate.errors ?? [])
    .map(({ instancePath, message }) => `${instancePath || '(root)'} ${message}`)
    .join('\n')
}
